import { Component, forwardRef, Input, OnDestroy, TemplateRef, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { OnChangeType, OnTouchedType } from 'ng-zorro-antd/core/types';
import { NzSelectComponent } from 'ng-zorro-antd/select';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { debounceTime, filter, switchMap, tap } from 'rxjs/operators';
import { catchAndContinue } from './catch-and-continue';
import { IGenericSelectSearchService, IGenericSelectService } from './generic-select';
import { trackByRef } from './track-by';

type KeysOfType<T, U> = Extract<
  keyof T,
  keyof {
    [K in keyof T as T[K] extends U ? K : never]: T[K];
  }
>;

@Component({
  selector: 'em-generic-select',
  templateUrl: './generic-select.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      // eslint-disable-next-line @angular-eslint/no-forward-ref
      useExisting: forwardRef(() => GenericSelectComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class GenericSelectComponent<TEntity> implements OnDestroy, ControlValueAccessor {
  public readonly trackByRef = trackByRef;
  public loading$ = new BehaviorSubject<boolean>(false);
  public entities$ = new BehaviorSubject<TEntity[]>([]);
  public selectedIds$ = new BehaviorSubject<unknown[]>([]);
  public isServerSideSearch = false;

  @Input() public placeholder: TemplateRef<any> | string | null = '';

  @Input() public valueKey?: keyof TEntity;
  @Input() public nameKey?: KeysOfType<TEntity, string>;
  @Input() public getNameFunction?: (entity: TEntity) => string;

  @Input() public mode: 'default' | 'multiple' | 'tags' = 'default';

  private searchTerm$?: Subject<string>;
  private select!: NzSelectComponent;
  private readonly subscriptions = new Subscription();

  @Input() public set service(service: IGenericSelectService<TEntity>) {
    this.loading$.next(true);

    this.subscriptions.add(
      service.getForSelect(this.selectedIds$).subscribe((entities) => {
        this.loading$.next(false);
        this.entities$.next(entities);
      })
    );

    if (!this.placeholder) {
      this.placeholder = `Select ${service.getEntityName()}`;
    }
  }

  @Input() public set search(service: IGenericSelectSearchService<TEntity>) {
    this.isServerSideSearch = true;
    const searchTerm$ = new Subject<string>();
    this.searchTerm$ = searchTerm$;

    this.subscriptions.add(
      searchTerm$
        .pipe(
          debounceTime(500),
          filter((searchText) => searchText.length > 0),
          tap(() => {
            this.loading$.next(true);
          }),
          switchMap((searchText) =>
            catchAndContinue(service.getForSearch(searchText), () => {
              this.loading$.next(false);
            })
          )
        )
        .subscribe((entities) => {
          this.loading$.next(false);
          this.entities$.next(entities);
        })
    );

    if (!this.placeholder) {
      this.placeholder = `Search for ${service.getEntityName()}`;
    }
  }

  @ViewChild('select', { static: true }) private set setSelect(select: NzSelectComponent) {
    select.nzMode = this.mode;
    this.select = select;
  }

  public getName(
    entity: TEntity,
    nameKey?: KeysOfType<TEntity, string>,
    getNameFunction?: (entity: TEntity) => string
  ): string | null {
    if (getNameFunction) {
      return getNameFunction(entity);
    }

    if (nameKey !== undefined) {
      const value = entity[nameKey];

      if (typeof value === 'string') {
        return value;
      }
    }

    // ngZorro expects null
    // eslint-disable-next-line no-null/no-null
    return null;
  }

  public getValue(entity: TEntity, valueKey?: keyof TEntity): TEntity[keyof TEntity] | undefined {
    if (!valueKey) {
      return undefined;
    }

    return entity[valueKey];
  }

  public onSearch(searchText: string): void {
    this.searchTerm$?.next(searchText);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  public writeValue(modelValue: any): void {
    this.select.writeValue(modelValue);
    this.selectedIds$.next(this.select.listOfValue);
  }

  public registerOnChange(fn: OnChangeType): void {
    this.select.registerOnChange(fn);
  }

  public registerOnTouched(fn: OnTouchedType): void {
    this.select.registerOnTouched(fn);
  }

  public setDisabledState(isDisabled: boolean): void {
    this.select.setDisabledState(isDisabled);
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
